/**
 * A colour for every region, derived in the UI (BON-538).
 *
 * The roster's dot and the map's pin have to agree, and they have to be
 * DISTINCT within a scope or the dot stops carrying information — which is
 * what inheriting the parent's colour produced: a regional view where every
 * row was the same teal.
 *
 * So the rule is positional rather than inherited. Sub-regions take their
 * colour from a fixed palette by their rank among their SIBLINGS: the first
 * sub-region of a region gets colour 1, the second colour 2, and so on. Two
 * regions can therefore reuse a colour between them, which is fine — nothing
 * shows two regions' sub-regions side by side without their parents.
 *
 * Top-level regions keep the colour stored on the row, because those are
 * assigned deliberately in Region Management and seeded per environment; the
 * palette only covers one that has none yet.
 *
 * Ranking is by NAME, not by creation order, so a region's colour is the same
 * in every environment and does not move when a sibling is added earlier in the
 * list.
 */
import type { RegionNode } from '../../../../api/regions';
import { ROOT_COLORS } from '../../admin/regions/RegionHierarchyTree';
import { byName } from '../../lib/compare';

/** Venues with no region at all, and any colour we cannot derive. */
export const UNASSIGNED_COLOR = '#8a948c';

/**
 * Sub-region colours, by rank among siblings. Fifteen is well past the five
 * metros we have and the handful a region plausibly grows to; past that it
 * wraps, which is the honest failure — a sixteenth sub-region shares a colour
 * rather than getting an unreadable one.
 *
 * Chosen to stay legible as an 8px dot on both the light and the dark card, and
 * to be tellable apart from their neighbours in the list.
 */
export const SUBREGION_COLORS = [
  '#2563eb', // blue
  '#ea580c', // orange
  '#16a34a', // green
  '#9333ea', // purple
  '#0891b2', // cyan
  '#dc2626', // red
  '#ca8a04', // gold
  '#db2777', // pink
  '#0d9488', // teal
  '#4f46e5', // indigo
  '#65a30d', // lime
  '#be123c', // rose
  '#0369a1', // deep blue
  '#a16207', // bronze
  '#7c3aed', // violet
];

/** Region id → the colour its dot and its pins are drawn in. */
export type RegionColors = ReadonlyMap<string, string>;

function walk(nodes: readonly RegionNode[], isRoot: boolean, into: Map<string, string>): void {
  const ranked = [...nodes].sort(byName);
  ranked.forEach((node, index) => {
    into.set(
      node.id,
      isRoot
        ? (node.color ?? ROOT_COLORS[index % ROOT_COLORS.length])
        : SUBREGION_COLORS[index % SUBREGION_COLORS.length],
    );
    walk(node.children, false, into);
  });
}

export function buildRegionColors(tree: readonly RegionNode[]): RegionColors {
  const colors = new Map<string, string>();
  walk(tree, true, colors);
  return colors;
}
